/**
 * Accordeonkoppen van de "per project"-weergave van de renovatietickets.
 *
 * Niveau 1 = renovatieproject, niveau 2 = renovatieonderdeel. De tellers komen
 * uit de backend-aggregatie (niet uit het aantal geladen rijen), zodat ze ook
 * correct blijven wanneer de ticketlimiet bereikt is.
 *
 * @module RenoHelpdesk/Components/RenoGroupHeaders
 */

import { useNavigate } from 'react-router-dom';
import { Buildings, Tag, Wall, Warning } from '@phosphor-icons/react';
import { Badge } from 'components/ui/badge';
import { RenoTicketPartGroup, RenoTicketProjectGroup } from '../types/renoTicket';

interface CountBadgesProps {
  openCount: number;
  completedCount: number;
  ticketCount: number;
}

/** Open / afgerond / totaal — zelfde volgorde als de statistieken bovenaan de pagina. */
const CountBadges = ({ openCount, completedCount, ticketCount }: CountBadgesProps) => (
  <div className="flex shrink-0 items-center gap-1.5">
    <Badge variant="secondary">{ticketCount} tickets</Badge>
    {openCount > 0 && <Badge>{openCount} open</Badge>}
    {completedCount > 0 && <Badge variant="outline">{completedCount} afgerond</Badge>}
  </div>
);

interface RenoProjectGroupHeaderProps {
  group: RenoTicketProjectGroup | undefined;
  /** Fallback wanneer de groepsmetadata (nog) niet beschikbaar is. */
  fallbackTitle?: string;
  fallbackCount?: number;
}

/** Kop van een renovatieproject-groep. */
export const RenoProjectGroupHeader = ({
  group,
  fallbackTitle = 'Onbekend project',
  fallbackCount = 0,
}: RenoProjectGroupHeaderProps) => {
  // De kop zit in de accordeon-trigger (een <button>); een <Link> daarin zou
  // ongeldige HTML-nesting geven, dus navigeren we programmatisch.
  const navigate = useNavigate();

  if (!group) {
    return (
      <span className="text-neutral-primary text-sm font-semibold">
        {fallbackTitle} ({fallbackCount})
      </span>
    );
  }

  return (
    <div className="flex min-w-0 flex-1 items-center gap-3">
      <Wall className="text-moss-primary h-4 w-4 shrink-0" weight="duotone" />
      <span className="text-neutral-primary min-w-0 truncate text-sm font-semibold">
        {group.projectTitle}
      </span>
      {group.projectId !== null && (
        <span
          className="shrink-0 cursor-pointer"
          title="Open het renovatieproject"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/renovation/${group.projectId}`);
          }}
        >
          <Badge variant="outline">RENO-{group.projectId}</Badge>
        </span>
      )}
      {group.buildingId && (
        <span className="text-neutral-secondary-mid flex shrink-0 items-center gap-1 text-xs">
          <Buildings className="h-3 w-3" />
          {group.buildingId}
        </span>
      )}
      {group.projectStatus && (
        <span className="text-neutral-secondary-mid hidden shrink-0 text-xs lg:inline">
          {group.projectStatus}
        </span>
      )}
      <div className="ml-auto">
        <CountBadges
          openCount={group.openCount}
          completedCount={group.completedCount}
          ticketCount={group.ticketCount}
        />
      </div>
    </div>
  );
};

interface RenoPartGroupHeaderProps {
  part: RenoTicketPartGroup | undefined;
  fallbackTitle?: string;
  fallbackCount?: number;
}

/** Kop van een renovatieonderdeel-groep binnen een project. */
export const RenoPartGroupHeader = ({
  part,
  fallbackTitle = 'Onbekend onderdeel',
  fallbackCount = 0,
}: RenoPartGroupHeaderProps) => {
  if (!part) {
    return (
      <span className="text-neutral-primary text-sm font-medium">
        {fallbackTitle} ({fallbackCount})
      </span>
    );
  }

  return (
    <div className="flex min-w-0 flex-1 items-center gap-3">
      <Tag className="text-neutral-secondary-mid h-4 w-4 shrink-0" />
      <span className="text-neutral-primary min-w-0 truncate text-sm font-medium">
        {part.partLabel || part.partTitle}
      </span>
      {part.partLabel && part.partLabel !== part.partTitle && (
        <span className="text-neutral-secondary-mid hidden min-w-0 truncate text-xs lg:inline">
          {part.partTitle}
        </span>
      )}
      {part.partStatus && (
        <span className="text-neutral-secondary-mid hidden shrink-0 text-xs xl:inline">
          {part.partStatus}
        </span>
      )}
      {part.truncated && (
        <span className="flex shrink-0 items-center gap-1 text-xs text-amber-700">
          <Warning className="h-3 w-3" />
          {part.tickets.length} van {part.ticketCount} geladen
        </span>
      )}
      <div className="ml-auto">
        <CountBadges
          openCount={part.openCount}
          completedCount={part.completedCount}
          ticketCount={part.ticketCount}
        />
      </div>
    </div>
  );
};
